import { useCallback, useState } from 'react';

import type { DisplayCreative } from '@feathr/blackbox';
import { flattenErrors } from '@feathr/hooks';

/** Parameters for the useCreativeModal hook. */
export interface IUseCreativeModalParams {
  /** The creative being edited. */
  creative?: DisplayCreative;
  /** Callback when the modal should close. */
  onClose: () => void;
  /** Optional callback when saving. */
  onSave?: () => void | Promise<void>;
  /** Fields to skip validation for. */
  skipValidationFields?: string[];
  /** External handler for Bannersnack editor (overrides internal handling). */
  onEditInBannersnack?: () => void;
}

/** Return type for the useCreativeModal hook. */
export interface IUseCreativeModalReturn {
  /** Whether the internal Bannersnack editor is open. */
  isBannersnackEditorOpen: boolean;
  /** Handler to cancel and close (discards dirty changes). */
  handleCancel: () => void;
  /** Handler to save and close. */
  handleSave: () => Promise<void>;
  /** Handler to open the Bannersnack editor. */
  handleOpenBannersnackEditor: () => void;
  /** Handler to close the Bannersnack editor. */
  handleCloseBannersnackEditor: () => void;
  /** Validation errors for the creative. */
  validationErrors: string[];
  /** The handler to use for "Edit in Bannersnack" button (external or internal). */
  bannersnackEditHandler: (() => void) | undefined;
  /** Whether internal Bannersnack editor should render. */
  shouldRenderInternalBannersnackEditor: boolean;
}

/**
 * Hook that manages CreativeModal state and handlers.
 *
 * Encapsulates:
 * - Bannersnack editor open/close state
 * - Cancel handler (discards dirty changes)
 * - Save handler
 * - Validation error computation
 *
 * @param params - Hook parameters
 * @returns State and handlers for the modal
 *
 * @example
 * const {
 *   isBannersnackEditorOpen,
 *   handleCancel,
 *   handleSave,
 *   validationErrors,
 * } = useCreativeModal({ creative, onClose, onSave });
 */
export function useCreativeModal({
  creative,
  onClose,
  onSave,
  skipValidationFields = [],
  onEditInBannersnack,
}: IUseCreativeModalParams): IUseCreativeModalReturn {
  const [isBannersnackEditorOpen, setIsBannersnackEditorOpen] = useState(false);

  const handleCancel = useCallback((): void => {
    creative?.discardDirty();
    setIsBannersnackEditorOpen(false);
    onClose();
  }, [creative, onClose]);

  const handleSave = useCallback(async (): Promise<void> => {
    await onSave?.();
    await creative?.patchDirty();
    onClose();
  }, [creative, onClose, onSave]);

  const handleOpenBannersnackEditor = useCallback((): void => {
    setIsBannersnackEditorOpen(true);
  }, []);

  const handleCloseBannersnackEditor = useCallback((): void => {
    setIsBannersnackEditorOpen(false);
  }, []);

  // Compute validation errors
  const allErrors = creative ? creative.validate([], false, 'grouped').errors : {};
  const filteredErrors = Object.fromEntries(
    Object.entries(allErrors).filter(([field]) => !skipValidationFields.includes(field)),
  );
  const validationErrors = flattenErrors(filteredErrors);

  // Determine which handler to use for Bannersnack edit button
  const bannersnackEditHandler = onEditInBannersnack ?? handleOpenBannersnackEditor;

  // Only show internal Bannersnack editor when no external handler is provided
  const shouldRenderInternalBannersnackEditor =
    isBannersnackEditorOpen && !!creative && !onEditInBannersnack;

  return {
    isBannersnackEditorOpen,
    handleCancel,
    handleSave,
    handleOpenBannersnackEditor,
    handleCloseBannersnackEditor,
    validationErrors,
    bannersnackEditHandler,
    shouldRenderInternalBannersnackEditor,
  };
}
